<template>
  <section class="account">
    <AppHeader />

    <div class="page">
      <button type="button" class="rec-link" @click="$router.push('/records')">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 7v10c0 1.7 3.6 3 8 3s8-1.3 8-3V7" /></svg>
        <span>{{ $t('assetRecord') }}</span>
      </button>

      <h1 class="total-title">{{ $t('totalAssets') }}</h1>
      <div class="big-num">{{ displayTotalAssets }}<span class="u"> {{ $quote }}</span></div>
      <div class="big-lines">
        <div>{{ $t('expectedOutput') }}: {{ displayExpectedOutput }} {{ $quote }} / 24h</div>
        <div>{{ $t('airdropLocked') }}: {{ displayAirdrop }} {{ $quote }}</div>
      </div>

      <div class="card stats">
        <div class="cell"><div class="k">{{ $t('todayProfit') }}</div><div class="v">{{ displayTodayIncome }} {{ $quote }}</div></div>
        <div class="cell"><div class="k">{{ $t('returnRate') }}</div><div class="v accent">{{ displayRate }}</div></div>
        <div class="cell"><div class="k">{{ $t('lockedBalance') }}</div><div class="v">{{ displayLocked }} {{ $quote }}</div></div>
        <div class="cell"><div class="k">{{ $t('withdrawable') }}</div><div class="v accent">{{ displayAvailable }} {{ $quote }}</div></div>
      </div>

      <h2 class="sec-title">{{ $t('withdraw') }}</h2>
      <div class="card wd">
        <input v-model="withdrawValue" type="text" class="wd-input" :placeholder="$t('enterAmount')" />
        <button type="button" class="wd-all" @click="fillAll">{{ $t('all') }}</button>
      </div>
      <p class="wd-tip">{{ $t('available') }}: {{ displayAvailable }} {{ $quote }}</p>
      <p v-if="message" class="msg">{{ message }}</p>
      <button type="button" class="primary-btn" :disabled="btnLoading" @click="onWithdraw">
        {{ btnLoading ? $t('processing') : $t('confirm') }}
      </button>

      <h2 class="sec-title">{{ $t('vipList') }}</h2>
      <div v-if="vipList.length" class="vip-list">
        <div v-for="row in vipList" :key="row.id" class="card vip-item">
          <div class="vip-head">
            <span class="vip-coin">{{ row.chainLabel }}{{ row.coin }}</span>
            <span class="vip-st" :class="row.statusClass">{{ vipStatusText(row.status) }}</span>
          </div>
          <div class="vip-row"><span class="k">{{ $t('vipMultiplier') }}</span><span class="v accent">{{ row.multiplierText }}</span></div>
          <div class="vip-row"><span class="k">{{ $t('vipDays') }}</span><span class="v">{{ row.days }} {{ $t('daysUnit') }}</span></div>
          <div class="vip-row"><span class="k">{{ $t('vipPledgeAmount') }}</span><span class="v">{{ row.pledgeAmountText }} {{ row.coin }}</span></div>
          <div v-if="row.approved" class="vip-row"><span class="k">{{ $t('vipExpireTime') }}</span><span class="v">{{ formatDate(row.expire_time) }}</span></div>
          <div v-if="row.approved" class="vip-row"><span class="k">{{ $t('vipSettled') }}</span><span class="v">{{ Number(row.settled) === 1 ? $t('yes') : $t('no') }}</span></div>
          <div v-if="row.pledge_txid" class="vip-row"><span class="k">{{ $t('vipTxid') }}</span><span class="v hash">{{ shortHash(row.pledge_txid) }}</span></div>
          <div class="vip-row"><span class="k">{{ $t('vipApplyTime') }}</span><span class="v">{{ formatDate(row.createtime) }}</span></div>
          <p v-if="row.remark" class="vip-remark">{{ row.remark }}</p>
        </div>
      </div>
      <div v-else class="card">
        <div class="no-data">
          <img src="/recovered-assets/no_data.svg" alt="no-data" />
          <p>{{ vipLoading ? $t('processing') : $t('noData') }}</p>
        </div>
      </div>
    </div>

    <MobileTabBar current="account" />
  </section>
</template>

<script>
import { getConfig, getUserAssets, getVipList, orderWithdraw } from "../recovered/trade-api";
import { getUserInfo } from "../recovered/sign-api";
import { getPromoteInfo } from "../recovered/share-api";
import MobileTabBar from "../components/MobileTabBar.vue";
import LangSwitch from "../components/LangSwitch.vue";
import QuoteSelect from "../components/QuoteSelect.vue";
import AppHeader from "../components/AppHeader.vue";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default {
  name: "AccountView",
  components: { MobileTabBar, LangSwitch, QuoteSelect, AppHeader },
  i18n: {
    zh: {
      assetRecord: "资产记录",
      totalAssets: "总资产",
      expectedOutput: "预期输出",
      airdropLocked: "空投待解锁",
      todayProfit: "今日收益",
      returnRate: "回报率",
      lockedBalance: "锁定余额",
      withdrawable: "可提现",
      withdraw: "提领",
      enterAmount: "请输入提币金额",
      all: "全部",
      available: "可用",
      confirm: "确认",
      processing: "处理中…",
      vipList: "VIP池列表",
      noData: "没有资料",
      invalidAmount: "请输入有效金额",
      insufficient: "余额不足",
      withdrawSucceeded: "提现成功",
      vipMultiplier: "出矿倍数",
      vipDays: "期限",
      vipPledgeAmount: "质押本金",
      vipExpireTime: "到期时间",
      vipSettled: "本金已返还",
      vipTxid: "质押哈希",
      vipApplyTime: "申请时间",
      vipPending: "审核中",
      vipApproved: "已通过",
      vipRejected: "已拒绝",
      daysUnit: "天",
      yes: "是",
      no: "否",
    },
    en: {
      assetRecord: "Asset Records",
      totalAssets: "Total Assets",
      expectedOutput: "Expected Output",
      airdropLocked: "Airdrop Locked",
      todayProfit: "Today's Profit",
      returnRate: "Return Rate",
      lockedBalance: "Locked Balance",
      withdrawable: "Withdrawable",
      withdraw: "Withdraw",
      enterAmount: "Enter withdrawal amount",
      all: "All",
      available: "Available",
      confirm: "Confirm",
      processing: "Processing…",
      vipList: "VIP Pool List",
      noData: "No data",
      invalidAmount: "Please enter a valid amount",
      insufficient: "Insufficient balance",
      withdrawSucceeded: "Withdraw succeeded",
      vipMultiplier: "Mining Multiplier",
      vipDays: "Term",
      vipPledgeAmount: "Pledged Principal",
      vipExpireTime: "Expires At",
      vipSettled: "Principal Returned",
      vipTxid: "Pledge Hash",
      vipApplyTime: "Applied At",
      vipPending: "Under Review",
      vipApproved: "Approved",
      vipRejected: "Rejected",
      daysUnit: "days",
      yes: "Yes",
      no: "No",
    },
  },
  data() {
    return {
      message: "",
      withdrawValue: "",
      btnLoading: false,
      config: { usdtDecimal: 2 },
      userInfo: {
        totalEarnings: 0,
        todayEarnings: 0,
        experienceGold: 0,
        rate: "",
        address: "",
      },
      teamInfo: { todayEthRevenue: 0, todayTrxRevenue: 0 },
      assets: {
        TRX: { balance: 0, pledgeBalance: 0, totalBalance: 0, airdropBalance: 0, expectedOutput: "", rate: "" },
        USDC: { balance: 0, pledgeBalance: 0, totalBalance: 0, airdropBalance: 0, expectedOutput: "", rate: "" },
        USDT: { balance: 0, pledgeBalance: 0, totalBalance: 0, airdropBalance: 0, expectedOutput: "", rate: "" },
      },
      vipList: [],
      vipLoading: false,
    };
  },
  computed: {
    fullAddress() {
      return this.userInfo.address || localStorage.getItem("U_ADDRESS") || sessionStorage.getItem("ADDRESS") || "";
    },
    displayAddress() {
      const a = this.fullAddress;
      if (!a) return "0x0000...0000";
      if (a.length <= 14) return a;
      return `${a.slice(0, 6)}...${a.slice(-4)}`;
    },
    quoteAsset() {
      return this.assets[this.$quote] || this.assets.USDC;
    },
    displayTotalAssets() {
      return this.numFormat(this.quoteAsset.totalBalance, 2);
    },
    displayAvailable() {
      return this.numFormat(this.quoteAsset.balance, 2);
    },
    displayLocked() {
      return this.numFormat(this.quoteAsset.pledgeBalance, 2);
    },
    todayEarnings() {
      // userinfo 返回的是下划线字段
      const raw = this.userInfo.today_earnings;
      return raw === undefined || raw === null ? this.userInfo.todayEarnings : raw;
    },
    displayExpectedOutput() {
      // 预期输出：优先 /api/user/wallet 当前币种的 expected_output
      const fromWallet = this.quoteAsset.expectedOutput;
      if (fromWallet !== undefined && fromWallet !== null && fromWallet !== "") {
        return this.numFormat(fromWallet, 2);
      }
      return this.numFormat(this.todayEarnings, 2);
    },
    displayAirdrop() {
      // 空投待解锁：/api/user/wallet 里当前币种的 airdrop_balance
      return this.numFormat(this.quoteAsset.airdropBalance, 2);
    },
    displayRate() {
      // 回报率：优先 /api/user/wallet 当前币种的 rate
      return this.quoteAsset.rate || this.userInfo.rate || "0.00%";
    },
    displayTodayIncome() {
      return this.numFormat(this.teamInfo.todayEthRevenue || this.todayEarnings, 2);
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    setMessage(msg) {
      this.message = msg || "";
    },
    numFormat(v, decimals = 2) {
      return toNum(v).toFixed(decimals);
    },
    normalizeAssets(raw) {
      // /api/user/wallet：data 为数组
      // [{ coin, balance, pledge_balance, total_balance, airdrop_balance, expected_output, rate }]
      const next = {
        TRX: { balance: 0, pledgeBalance: 0, totalBalance: 0, airdropBalance: 0, expectedOutput: "", rate: "" },
        USDC: { balance: 0, pledgeBalance: 0, totalBalance: 0, airdropBalance: 0, expectedOutput: "", rate: "" },
        USDT: { balance: 0, pledgeBalance: 0, totalBalance: 0, airdropBalance: 0, expectedOutput: "", rate: "" },
      };
      const list = Array.isArray(raw) ? raw : raw && Array.isArray(raw.list) ? raw.list : [];
      list.forEach((x) => {
        const c = x && x.coin;
        if (!c || !next[c]) return;
        const expected = x.expected_output;
        next[c] = {
          coin: c,
          balance: toNum(x.balance),
          pledgeBalance: toNum(x.pledge_balance),
          totalBalance: toNum(x.total_balance),
          airdropBalance: toNum(x.airdrop_balance),
          // 预期输出 / 回报率按币种返回，跟随顶部计价币展示
          expectedOutput: expected === undefined || expected === null ? "" : expected,
          rate: x.rate || "",
        };
      });
      this.assets = next;
    },
    normalizeVipList(raw) {
      const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.list) ? raw.list : []);
      this.vipList = list.map((row, idx) => {
        const status = Number(row && row.status);
        const chain = String((row && row.chain) || "").toUpperCase();
        const multiplier = toNum(row && row.multiplier);
        return {
          ...row,
          id: (row && row.id) || `vip_${idx}`,
          coin: String((row && row.coin) || "").toUpperCase(),
          // 有链就显示成「ERC · USDT」，没有就只显示币种
          chainLabel: chain ? `${chain} · ` : "",
          status,
          approved: status === 1,
          statusClass: status === 1 ? "st-ok" : status === 2 ? "st-bad" : "st-wait",
          multiplierText: multiplier > 0 ? `${multiplier}X` : "--",
          pledgeAmountText: this.numFormat(row && row.pledge_amount, 2),
          days: toNum(row && row.days),
        };
      });
    },
    vipStatusText(status) {
      const n = Number(status);
      if (n === 1) return this.$t("vipApproved");
      if (n === 2) return this.$t("vipRejected");
      return this.$t("vipPending");
    },
    formatDate(input) {
      if (!input) return "--";
      // 后端已返回格式化字符串时直接展示
      if (typeof input === "string" && !/^\d+$/.test(input.trim())) return input;
      let ts = Number(input);
      if (!Number.isFinite(ts) || ts <= 0) return "--";
      if (ts < 1e12) ts *= 1000; // Unix 秒 → 毫秒
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return "--";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const h = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day} ${h}:${mm}`;
    },
    shortHash(hash) {
      const h = String(hash || "");
      if (h.length <= 18) return h;
      return `${h.slice(0, 10)}...${h.slice(-8)}`;
    },
    async loadVipList() {
      this.vipLoading = true;
      try {
        this.normalizeVipList(await getVipList());
      } catch (err) {
        this.vipList = [];
      } finally {
        this.vipLoading = false;
      }
    },
    async bootstrap() {
      try {
        const cfg = await getConfig(document.domain);
        if (cfg && typeof cfg === "object") this.config = { ...this.config, ...cfg };
      } catch (err) {
        // ignore
      }

      try {
        const info = await getUserInfo();
        this.userInfo = { ...this.userInfo, ...(info || {}) };
      } catch (err) {
        // ignore
      }

      try {
        const team = await getPromoteInfo();
        this.teamInfo = {
          todayEthRevenue: toNum(team && (team.eth_today_income || team.todayEthRevenue)),
          todayTrxRevenue: toNum(team && (team.trx_today_income || team.todayTrxRevenue)),
        };
      } catch (err) {
        // ignore
      }

      try {
        const assets = await getUserAssets();
        this.normalizeAssets(assets);
      } catch (err) {
        // ignore
      }

      this.loadVipList();
    },
    fillAll() {
      this.withdrawValue = String(toNum(this.quoteAsset.balance) || 0);
    },
    async onWithdraw() {
      const amount = toNum(this.withdrawValue);
      if (amount <= 0) {
        this.setMessage(this.$t("invalidAmount"));
        return;
      }
      if (amount > toNum(this.quoteAsset.balance)) {
        this.setMessage(this.$t("insufficient"));
        return;
      }
      this.btnLoading = true;
      this.setMessage("");
      try {
        // coin 跟随顶部选择的计价币（USDT / USDC）
        await orderWithdraw(amount, this.$quote);
        this.withdrawValue = "";
        this.setMessage(this.$t("withdrawSucceeded"));
        const assets = await getUserAssets();
        this.normalizeAssets(assets);
      } catch (err) {
        this.setMessage((err && err.message) || String(err));
      } finally {
        this.btnLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.account {
  min-height: 100vh;
  padding-bottom: 92px;
  background: transparent;
}

.topbar {
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
}

.addr {
  max-width: 120px;
  height: 30px;
  line-height: 30px;
  padding: 0 14px;
  border-radius: 15px;
  background: linear-gradient(120deg, #3f7bff, #4b86ff);
  color: #fff;
  font-size: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.page {
  padding: 4px 14px 0;
}

.rec-link {
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin: 0 0 8px auto;
  border: 0;
  background: transparent;
  color: var(--pool-muted);
  font-size: 13px;
  cursor: pointer;
}

.rec-link svg {
  width: 17px;
  height: 17px;
  stroke: var(--pool-gold);
  fill: none;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* 总资产 */
.total-title {
  text-align: center;
  margin: 6px 0 14px;
  font-size: 20px;
  font-weight: 700;
  color: var(--pool-text);
}

.big-num {
  text-align: center;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: var(--pool-text);
}

.big-num .u {
  font-size: 15px;
  font-weight: 600;
  color: var(--pool-muted);
}

.big-lines {
  margin-top: 12px;
  text-align: center;
  color: var(--pool-muted);
  font-size: 13px;
  line-height: 1.7;
}

/* 卡片 */
.card {
  margin-top: 18px;
  background: var(--pool-glass);
  border: 1px solid var(--pool-line);
  border-radius: var(--pool-r);
  box-shadow: 0 20px 40px -34px rgba(0, 0, 0, 0.9);
}

.stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 12px;
  padding: 22px 18px;
}

.cell .k {
  color: var(--pool-muted);
  font-size: 13px;
}

.cell .v {
  margin-top: 8px;
  font-size: 18px;
  font-weight: 700;
  color: var(--pool-text);
  font-variant-numeric: tabular-nums;
}

.cell .v.accent {
  background: var(--pool-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* 区块标题 */
.sec-title {
  text-align: center;
  margin: 30px 0 14px;
  font-size: 20px;
  font-weight: 700;
  color: var(--pool-text);
}

/* 提领 */
.wd {
  display: flex;
  align-items: center;
  padding: 15px 16px;
}

.wd-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--pool-text);
  font-size: 16px;
  outline: none;
}

.wd-input::placeholder {
  color: var(--pool-faint);
}

.wd-all {
  border: 0;
  background: transparent;
  color: var(--pool-text);
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
}

.wd-tip {
  margin: 10px 4px 0;
  color: var(--pool-muted);
  font-size: 13px;
}

.msg {
  margin: 8px 4px 0;
  color: var(--pool-bad);
  font-size: 12px;
}

.primary-btn {
  display: block;
  width: 100%;
  margin-top: 14px;
  height: 46px;
  border: 0;
  border-radius: 12px;
  background: var(--pool-grad);
  color: #04121a;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 14px 30px -16px rgba(51, 226, 199, 0.5);
  cursor: pointer;
}

.primary-btn:disabled {
  opacity: 0.75;
}

/* VIP 池列表 */
.vip-item {
  padding: 16px 18px;
}

.vip-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--pool-line);
}

.vip-coin {
  font-size: 15px;
  font-weight: 700;
  color: var(--pool-text);
}

.vip-st {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  background: rgba(150, 170, 225, 0.14);
  color: var(--pool-muted);
}

.vip-st.st-ok {
  color: var(--pool-teal);
  background: rgba(51, 226, 199, 0.14);
}

.vip-st.st-bad {
  color: var(--pool-bad);
  background: rgba(251, 111, 134, 0.14);
}

.vip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  font-size: 13px;
}

.vip-row .k {
  color: var(--pool-muted);
}

.vip-row .v {
  color: var(--pool-text);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.vip-row .v.accent {
  background: var(--pool-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.vip-row .v.hash {
  font-weight: 500;
  word-break: break-all;
}

.vip-remark {
  margin: 12px 0 0;
  color: var(--pool-muted);
  font-size: 12px;
  line-height: 1.6;
}

.no-data {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.no-data img {
  width: 130px;
  height: auto;
  opacity: 0.9;
}

.no-data p {
  margin: 0;
  color: var(--pool-muted);
  font-size: 13px;
}
</style>
